import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { createAppointment } from '@utils/api';
import * as Sentry from '@sentry/react';
import { emitWidgetEvent } from '@utils/widgetEvents';
import { addFlowBreadcrumb } from '@utils/breadcrumbs';

// Define the step types
export type BookingStep =
  | 'serviceSelection'
  | 'screeningForm'
  | 'appointmentSelection'
  | 'patientInformation'
  | 'patientConfirmation'
  | 'complete';

// Define the booking data structure
export interface BookingData {
  // Service data (ID only - full service object is derived from API hooks)
  selectedServiceId?: string;

  // Patient information
  firstName: string;
  middleName: string;
  preferredFirstName: string;
  lastName: string;
  phone: string;
  email: string;
  platformPreference: 'sms' | 'email';
  smsOptIn: boolean;
  languagePreference: string;
  
  // Appointment data
  appointmentsAttributes: [{
    scheduledAt: string;
    operatorId: string;
    serviceId: string;
  }];
  
  // Screening form data
  responsesAttributes: any[];
  patientChartAttributesAttributes: any[];
}

// Type for step validation functions
type StepValidationFn = () => Promise<boolean>;

interface BookingContextType {
  // Current step management
  currentStep: BookingStep;
  visitedSteps: BookingStep[];
  canGoBack: boolean;

  // Submission state
  isSubmitting: boolean;
  submissionError: string | null;

  // Navigation functions
  goToStep: (step: BookingStep) => void;
  goBack: () => void;
  goForward: (step: BookingStep) => void;
  restart: () => void;

  // Submission functions
  submitAppointment: () => Promise<void>;
  retrySubmission: () => Promise<void>;

  // Data management
  bookingData: BookingData;
  updateBookingData: (data: Partial<BookingData>) => void;

  // Utility functions
  isCurrentStep: (step: BookingStep) => boolean;
  getStepTitle: (step: BookingStep) => string;

  // Step validation registration
  registerStepValidation: (step: BookingStep, validateFn: StepValidationFn) => void;
  unregisterStepValidation: (step: BookingStep) => void;
  validateCurrentStep: () => Promise<boolean>;

  // Click wrap tracking (for single service flow)
  clickWrapShown: boolean;
  setClickWrapShown: (shown: boolean) => void;

  screeningStartedAt: string | null;
}

const initialBookingData: BookingData = {
  firstName: '',
  middleName: '',
  preferredFirstName: '',
  lastName: '',
  phone: '',
  email: '',
  platformPreference: 'sms',
  smsOptIn: false,
  languagePreference: 'en',
  appointmentsAttributes: [{
    scheduledAt: '',
    operatorId: '',
    serviceId: '',
  }],
  responsesAttributes: [],
  patientChartAttributesAttributes: []
};

const BookingContext = createContext<BookingContextType | null>(null);

interface BookingProviderProps {
  children: ReactNode;
}

export const BookingProvider: React.FC<BookingProviderProps> = ({ children }) => {
  const { t, i18n } = useTranslation();

  const [visitedSteps, setVisitedSteps] = useState<BookingStep[]>(['serviceSelection']);
  const [bookingData, setBookingData] = useState<BookingData>({
    ...initialBookingData,
    languagePreference: i18n.language
  });

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  // Click wrap tracking (for single service flow)
  const [clickWrapShown, setClickWrapShown] = useState(false);

  const [screeningStartedAt, setScreeningStartedAt] = useState<string | null>(null);

  // Step validation registry - stores validation functions for each step
  const validationRegistry = React.useRef<Map<BookingStep, StepValidationFn>>(new Map());

  // Update language preference when i18n language changes
  React.useEffect(() => {
    setBookingData(prev => ({
      ...prev,
      languagePreference: i18n.language
    }));
  }, [i18n.language]);

  const currentStep = visitedSteps[visitedSteps.length - 1];

  // The screening form remounts empty on every entry, forward or back, so each
  // entry is a fresh sitting for the fill-time reported with the submission.
  useEffect(() => {
    if (currentStep === 'screeningForm') {
      setScreeningStartedAt(new Date().toISOString());
    }
  }, [currentStep]);
  const canGoBack = visitedSteps.length > 1 && currentStep !== 'complete';

  const goToStep = useCallback((step: BookingStep) => {
    addFlowBreadcrumb('Booking step entered', { step });
    setVisitedSteps(prev => [...prev, step]);
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  const goBack = useCallback(() => {
    addFlowBreadcrumb('Booking step back');
    setVisitedSteps(prev => prev.length > 1 ? prev.slice(0, -1) : prev);
  }, []);

  // Centralized submission method
  const submitAppointment = useCallback(async () => {
    setIsSubmitting(true);
    setSubmissionError(null);
    
    try {
      // Prepare the final booking data
      const finalData = {
        ...bookingData,
        languagePreference: i18n.language,
      };

      // Submit the appointment
      await createAppointment(finalData);
      emitWidgetEvent('appointmentSubmitted');
    } catch (err) {
      Sentry.captureException(err);
      setSubmissionError(t('booking.submissionErrorMessage'));
      // Still navigate to complete step to show error state
      goToStep('complete');
    } finally {
      setIsSubmitting(false);
    }
  }, [bookingData, i18n.language, goToStep, t]);

  // Retry submission method
  const retrySubmission = useCallback(async () => {
    await submitAppointment();
  }, [submitAppointment]);

  const goForward = useCallback(async (step: BookingStep) => {
    if (step === 'screeningForm') {
      emitWidgetEvent('screeningStarted');
    }
    // If moving to complete step, handle submission
    if (step === 'complete') {
      goToStep('complete');
      await submitAppointment();
    } else {
      // For other steps, just navigate normally
      goToStep(step);
    }
  }, [goToStep, submitAppointment]);

  const restart = useCallback(() => {
    setVisitedSteps(['serviceSelection']);
    setBookingData(prev => ({
      ...initialBookingData,
      languagePreference: prev.languagePreference
    }));
    setIsSubmitting(false);
    setSubmissionError(null);
    setScreeningStartedAt(null);
  }, []);

  const updateBookingData = useCallback((data: Partial<BookingData>) => {
    setBookingData(prev => ({ ...prev, ...data }));
  }, []);

  const isCurrentStep = useCallback((step: BookingStep) => {
    return currentStep === step;
  }, [currentStep]);

  const getStepTitle = useCallback((step: BookingStep) => {
    const stepTitles = {
      serviceSelection: t('steps.serviceSelection'),
      screeningForm: t('steps.screeningForm'),
      appointmentSelection: t('steps.appointmentSelection'),
      patientInformation: t('steps.patientInformation'),
      patientConfirmation: t('steps.patientConfirmation'),
      complete: t('steps.complete')
    };
    return stepTitles[step] || '';
  }, [t]);

  // Step validation methods
  const registerStepValidation = useCallback((step: BookingStep, validateFn: StepValidationFn) => {
    validationRegistry.current.set(step, validateFn);
  }, []);

  const unregisterStepValidation = useCallback((step: BookingStep) => {
    validationRegistry.current.delete(step);
  }, []);

  const validateCurrentStep = useCallback(async (): Promise<boolean> => {
    const validateFn = validationRegistry.current.get(currentStep);
    if (validateFn) {
      return await validateFn();
    }
    return true; // No validation registered = valid
  }, [currentStep]);

  const contextValue: BookingContextType = {
    currentStep,
    visitedSteps,
    canGoBack,
    isSubmitting,
    submissionError,
    goToStep,
    goBack,
    goForward,
    restart,
    submitAppointment,
    retrySubmission,
    bookingData,
    updateBookingData,
    isCurrentStep,
    getStepTitle,
    registerStepValidation,
    unregisterStepValidation,
    validateCurrentStep,
    clickWrapShown,
    setClickWrapShown,
    screeningStartedAt
  };

  return (
    <BookingContext.Provider value={contextValue}>
      {children}
    </BookingContext.Provider>
  );
};

export const useBookingContext = () => {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error('useBookingContext must be used within a BookingProvider');
  }
  return context;
};
